import { useState } from 'react';
import { Link } from 'react-router-dom';
import faqData from '@data/guides/faq.json';

const FAQ = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const categories = faqData.categories;

  const filteredQuestions = faqData.questions.filter(q => {
    const matchesCategory = selectedCategory === 'all' || q.category === selectedCategory;
    const matchesSearch = searchTerm === '' ||
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="container-custom">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Frequently Asked Questions (FAQ)
        </h1>
        <p className="text-lg text-gray-600 mb-6">
          Quick answers to your questions about medical treatment in China
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 mb-12">
        <aside className="lg:w-72 flex-shrink-0">
          <div className="sticky top-24 bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-lg font-bold mb-4">Categories</h2>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search questions..."
                className="input-field w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <nav className="space-y-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{category.name}</span>
                    <span className="text-sm opacity-75">
                      {faqData.questions.filter(q => q.category === category.id).length}
                    </span>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <main className="flex-1">
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">
              {categories.find(c => c.id === selectedCategory)?.name || 'All Questions'}
            </h2>
            <p className="text-sm text-gray-600">
              Showing {filteredQuestions.length} questions
            </p>
          </div>

          {filteredQuestions.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-4">No questions found matching your search.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory(null);
                }}
                className="btn-secondary"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredQuestions.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}
                    className="w-full flex items-start justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-1 rounded-full">
                          {categories.find(c => c.id === faq.category)?.name}
                        </span>
                        <span className="font-medium">{faq.question}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {faq.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-600 flex-shrink-0 ml-4 transition-transform ${
                        expandedId === faq.id ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {expandedId === faq.id && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50">
                      <div className="prose prose-sm text-gray-700">
                        {faq.answer.split('\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-2">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      <section className="bg-primary-50 border border-primary-200 rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Can't Find Your Answer?</h2>
        <p className="text-gray-700 mb-4">
          If you don't see your question here, we're here to help. Our team can provide personalized guidance for your specific situation.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/services"
            className="btn-primary flex items-center justify-center gap-2"
          >
            <span>Book a Consultation</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <a
            href="mailto:support@chinahospital.com"
            className="btn-secondary flex items-center justify-center gap-2"
          >
            <span>Email Us</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a22 22 0 0021.538 12.98 0L12 2c0 4.336 3.248 9 8 9a8 8 0 0010-10.34L10.892 5.8a22 22 0 00-7.892-2.268z" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
